import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Send, CheckCircle2, Loader2, Trash2,
  MessageCircle, Mail, Smartphone, Users, TrendingUp, Radio,
} from 'lucide-react';
import { useCampaigns, useCampaignStats } from '../hooks/useCampaigns';
import { useCreateCampaign, useDeleteCampaign } from '../hooks/useCampaignMutations';
import { CampaignForm } from './CampaignForm';
import { CampaignStatusBadge, CampaignTypeBadge } from './CampaignStatusBadge';
import type { Campaign, CampaignInsert } from '../types';

/* ─── Card de stat ───────────────────────────────────────────── */
function StatCard({ label, value, icon: Icon, accent = false }: {
  label: string; value: string | number | undefined; icon: React.ElementType; accent?: boolean;
}) {
  return (
    <div className={`card-premium rounded-2xl px-5 py-4 flex items-center gap-4 ${accent ? 'border-accent/20 shadow-glow-xs' : ''}`}>
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
        style={{
          background: accent
            ? 'linear-gradient(135deg, color-mix(in oklab,var(--color-accent),transparent 70%), color-mix(in oklab,var(--color-accent),transparent 85%))'
            : 'color-mix(in oklab,var(--color-ink),transparent 92%)',
        }}
      >
        <Icon className="h-5 w-5" style={{ color: accent ? 'var(--color-accent)' : 'var(--color-ink-muted)' }} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold text-ink tabular-nums">{value ?? '—'}</p>
        <p className="text-xs text-ink-muted mt-0.5">{label}</p>
      </div>
    </div>
  );
}

/* ─── Linha da tabela ────────────────────────────────────────── */
function CampaignRow({ campaign, onDelete }: { campaign: Campaign; onDelete: (id: string) => void }) {
  const navigate = useNavigate();
  const deliveryRate = campaign.stats_sent > 0
    ? Math.round((campaign.stats_delivered / campaign.stats_sent) * 100)
    : null;

  const TypeIcon = campaign.type === 'whatsapp' ? MessageCircle
    : campaign.type === 'email' ? Mail : Smartphone;

  return (
    <tr
      onClick={() => navigate(`/app/campanhas/${campaign.id}`)}
      className="border-b border-hairline/50 hover:bg-white/[0.03] transition-colors cursor-pointer group"
    >
      {/* Nome */}
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-xl bg-midnight-3 hairline flex items-center justify-center">
            <TypeIcon className="h-4 w-4 text-ink-muted" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-semibold text-sm text-ink truncate">{campaign.name}</p>
              {campaign.status === 'running' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/app/campanhas/${campaign.id}/war-room`);
                  }}
                  className="shrink-0 flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/25 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-amber-300 hover:bg-amber-500/25 transition-colors cursor-pointer animate-pulse"
                  title="Abrir War Room"
                >
                  <Radio className="h-2.5 w-2.5" />
                  LIVE
                </button>
              )}
            </div>
            {campaign.description && (
              <p className="text-[11px] text-ink-subtle truncate max-w-[280px]">{campaign.description}</p>
            )}
          </div>
        </div>
      </td>

      {/* Tipo */}
      <td className="px-4 py-3.5 hidden sm:table-cell">
        <CampaignTypeBadge type={campaign.type} />
      </td>

      {/* Status */}
      <td className="px-4 py-3.5">
        <CampaignStatusBadge status={campaign.status} />
      </td>

      {/* Audiência */}
      <td className="px-4 py-3.5 hidden md:table-cell">
        <div className="flex items-center gap-1.5 text-sm text-ink-muted">
          <Users className="h-3.5 w-3.5" />
          <span>{campaign.stats_targeted > 0 ? campaign.stats_targeted.toLocaleString('pt-BR') : '—'}</span>
        </div>
      </td>

      {/* Taxa de entrega */}
      <td className="px-4 py-3.5 hidden lg:table-cell">
        {deliveryRate !== null ? (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-20 rounded-full bg-midnight-4 overflow-hidden">
              <div
                className={`h-full rounded-full ${deliveryRate >= 90 ? 'bg-emerald-400' : deliveryRate >= 70 ? 'bg-amber-400' : 'bg-danger'}`}
                style={{ width: `${deliveryRate}%` }}
              />
            </div>
            <span className="font-mono text-xs text-ink-muted">{deliveryRate}%</span>
          </div>
        ) : (
          <span className="text-ink-subtle text-sm">—</span>
        )}
      </td>

      {/* Data */}
      <td className="px-4 py-3.5 hidden xl:table-cell text-xs text-ink-subtle">
        {campaign.scheduled_at
          ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(campaign.scheduled_at))
          : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(campaign.created_at))}
      </td>

      {/* Ações */}
      <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
        {campaign.status === 'draft' && (
          <button
            onClick={() => { if (confirm(`Remover "${campaign.name}"?`)) onDelete(campaign.id); }}
            className="rounded-lg p-1.5 text-ink-subtle hover:text-danger hover:bg-danger/10 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

/* ─── Página principal ───────────────────────────────────────── */
export function CampaignsPage() {
  const [formOpen, setFormOpen] = useState(false);
  const { data: campaigns = [], isLoading } = useCampaigns();
  const { data: stats } = useCampaignStats();
  const createMutation = useCreateCampaign();
  const deleteMutation = useDeleteCampaign();

  const handleCreate = (data: CampaignInsert) => {
    createMutation.mutate(data, { onSuccess: () => setFormOpen(false) });
  };

  return (
    <div className="flex flex-col gap-8 px-4 sm:px-6 py-6 max-w-screen-xl mx-auto w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Campanhas</h1>
          <p className="text-sm text-ink-muted mt-1">Disparos de WhatsApp, e-mail e SMS para sua base.</p>
        </div>
        <button
          onClick={() => setFormOpen(true)}
          className="flex items-center gap-2 btn-accent rounded-xl px-5 py-2.5 text-sm font-semibold self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          Nova campanha
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total de campanhas"  value={stats?.total}     icon={Send}         accent />
        <StatCard label="Em execução"         value={stats?.running}   icon={Loader2}      />
        <StatCard label="Concluídas"          value={stats?.completed} icon={CheckCircle2} />
        <StatCard label="Taxa de entrega"
          value={stats?.deliveryRate != null ? `${stats.deliveryRate}%` : '—'}
          icon={TrendingUp}
        />
      </div>

      {/* Tabela */}
      <div className="card-premium rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center gap-3 py-20 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Carregando campanhas…</span>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="h-16 w-16 rounded-2xl bg-midnight-3 hairline flex items-center justify-center">
              <Send className="h-7 w-7 text-ink-subtle" />
            </div>
            <div>
              <p className="font-display text-base font-bold text-ink">Nenhuma campanha ainda</p>
              <p className="text-sm text-ink-muted mt-1">Crie seu primeiro disparo em segundos.</p>
            </div>
            <button
              onClick={() => setFormOpen(true)}
              className="flex items-center gap-2 btn-accent rounded-xl px-5 py-2.5 text-sm font-semibold"
            >
              <Plus className="h-4 w-4" />
              Nova campanha
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-midnight-2/30">
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle">Campanha</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden sm:table-cell">Canal</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle">Status</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden md:table-cell">Audiência</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden lg:table-cell">Entrega</th>
                  <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle hidden xl:table-cell">Data</th>
                  <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle">Ações</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <CampaignRow
                    key={c.id}
                    campaign={c}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de criação — usa createPortal internamente */}
      {formOpen && (
        <CampaignForm
          onSubmit={handleCreate}
          onClose={() => setFormOpen(false)}
          isPending={createMutation.isPending}
        />
      )}
    </div>
  );
}
